import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, LogOut, ChevronRight, RefreshCw } from 'lucide-react';
import { Spinner } from '@tendedloop/ui';
import { Avatar } from '@/components/ui/Avatar';
import { LanguageSwitcher } from '@/components/ui/LanguageSwitcher';
import { useAuthStore } from '@/stores/authStore';
import { useTaskStore } from '@/stores/taskStore';

export function Profile() {
  const { t } = useTranslation(['operator', 'common']);
  const navigate = useNavigate();

  const {
    user,
    tenantName,
    isLoading: authLoading,
    logout,
    checkAuth,
  } = useAuthStore();

  const {
    stats,
    fetchStats,
  } = useTaskStore();

  // Fetch user info and stats on mount
  useEffect(() => {
    checkAuth();
    fetchStats();
  }, [checkAuth, fetchStats]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    { icon: Bell, label: t('profile.notificationSettings'), path: '/profile/notifications' },
  ];

  if (authLoading && !user) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="px-4 py-6">
      {/* Profile Header */}
      <div className="flex items-center gap-4 mb-8">
        <Avatar name={user?.name || 'User'} size="lg" />
        <div>
          <h1 className="text-xl font-semibold text-neutral-dark">
            {user?.name || 'User'}
          </h1>
          <p className="text-neutral-medium">{user?.email}</p>
          {tenantName && (
            <p className="text-sm text-neutral-medium">{tenantName}</p>
          )}
        </div>
      </div>

      {/* Stats Summary */}
      <div className="bg-white border border-neutral-light rounded-xl p-4 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-neutral-medium tracking-wider">
            {t('profile.todaysSummary')}
          </h2>
          <button
            onClick={() => fetchStats()}
            className="p-1 text-neutral-medium hover:text-neutral-dark transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-primary font-mono">{stats.completedToday}</p>
            <p className="text-xs text-neutral-medium">{t('profile.completed')}</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-secondary font-mono">{stats.new}</p>
            <p className="text-xs text-neutral-medium">{t('profile.new')}</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-accent-green font-mono">{stats.inProgress}</p>
            <p className="text-xs text-neutral-medium">{t('profile.inProgress')}</p>
          </div>
        </div>
      </div>

      {/* Menu Items */}
      <div className="bg-white border border-neutral-light rounded-xl overflow-hidden mb-8">
        {menuItems.map(item => {
          const Icon = item.icon;
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className="w-full flex items-center justify-between px-4 py-4 border-b border-neutral-light last:border-0 hover:bg-neutral-light/30 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Icon className="h-5 w-5 text-neutral-medium" />
                <span className="text-neutral-dark">{item.label}</span>
              </div>
              <ChevronRight className="h-5 w-5 text-neutral-medium" />
            </button>
          );
        })}
      </div>

      {/* Language Settings */}
      <LanguageSwitcher className="mb-6" />

      {/* Logout */}
      <button
        onClick={handleLogout}
        className="w-full flex items-center gap-3 px-4 py-4 text-error bg-white border border-neutral-light rounded-xl hover:bg-error/5 transition-colors"
      >
        <LogOut className="h-5 w-5" />
        {t('profile.signOut')}
      </button>
    </div>
  );
}
